<script setup lang="ts">
import { onMounted, ref } from 'vue';
import {
  getRegisterPresentationData,
  imageSize,
  imageUrl,
  imageMime,
} from 'src/modules/User/views/useGetRegisterPresentationData';
import type { ApiRegisterPresentationRegisterPresentation } from 'src/types/contentTypes';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import type { AxiosError } from 'axios';

const { locale } = useI18n();

const router = useRouter();
const data = ref<ApiRegisterPresentationRegisterPresentation | null>(null);
const showSkeleton = ref(true);

onMounted(async () => {
  try {
    // Load data without awaiting to avoid blocking the mount flow
    data.value = await getRegisterPresentationData(locale.value);
  } catch (e) {
    const errorMessage =
      (e as AxiosError).status === 404
        ? `Failed to load presentation data for locale ${locale.value}`
        : (e as AxiosError).message;
    void router.push({ name: 'users.register' });
    throw new Error(errorMessage, { cause: e });
  }
});

const handleImageLoad = () => {
  showSkeleton.value = false;
};

const handleImageError = (e: Event) => {
  console.warn('Failed to load presentation image', e);
  showSkeleton.value = false; // avoid leaving the UI blank
};
</script>

<template>
  <div class="presentation-container">
    <!-- Loading overlay: visible until both data and the image are ready -->

    <!-- Picture element with image -->
    <picture
      v-if="data"
      class="background-image"
    >
      <source
        :srcset="`
        ${imageUrl('image', 'small')} ${imageSize('image', 'small')}w,
        ${imageUrl('image', 'medium')} ${imageSize('image', 'medium')}w,
        ${imageUrl('image', 'large')} ${imageSize('image', 'large')}w,
        ${imageUrl('image', 'original')} ${imageSize('image', 'original')}w,
        `"
        :sizes="`
        (max-width: ${imageSize('image', 'small')}px) ${imageSize('image', 'small')}px,
        (max-width: ${imageSize('image', 'medium')}px) ${imageSize('image', 'medium')}px,
        (max-width: ${imageSize('image', 'large')}px) ${imageSize('image', 'large')}px,
        ${imageSize('image', 'original')}px,
        `"
        :type="imageMime('image')"
      />

      <img
        ref="imgRef"
        :src="`${imageUrl('image', 'original')}`"
        alt="Presentation"
        @click="router.push({ name: 'users.register' })"
        @load="handleImageLoad"
        @error="handleImageError"
      />
    </picture>
  </div>
</template>

<style scoped lang="scss">
.presentation-container {
  position: relative;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  background-color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.background-image {
  position: relative;
  width: 100%;
  height: auto;

  img {
    display: block;
    width: 100%;
    height: auto;
    cursor: pointer;
  }
}

.skeleton-container {
  position: fixed; // ensure overlay is always on top
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 9999;
}
</style>
